import { createTheme } from "@mui/material/styles";

// Define custom color palette
const baseColors = {
  defaultBackground: "#FFFFFF",
  primary: {
    0: "#CDE4FF",
    1: "#A4CEFF",
    2: "#7AB8FF",
    3: "#52A2FF",
    4: "#288BFE",
    5: "#0C6FE2",
    6: "#0056B9",
    7: "#003878",
    8: "#001834",
  },
  secondary: {
    0: "#F8F8FB",
    1: "#E5E5EA",
    2: "#D1D1D6",
    3: "#C7C7CC",
    4: "#AEAEB2",
    5: "#8E8E93",
    6: "#636366",
    7: "#48484A",
    8: "#3A3A3C",
  },
  text: {
    0: "#8E8E93",
    1: "#636366",
    2: "#48484A",
    3: "#3A3A3C",
    4: "#2C2C2E",
    5: "#1C1C1E",
    6: "#09090A",
    7: "#09090A",
    8: "#FCFCFC",
  },
  red: "#FF584E",
  orange: "#FF9914",
  green: "#2DD282",
  grey: "#F2F2F7",
};

// Extend the Material-UI theme interface
declare module "@mui/material/styles" {
  interface Theme {
    colors: typeof baseColors;
  }

  interface ThemeOptions {
    colors?: typeof baseColors;
  }
}

// Extend Button variant types
declare module "@mui/material/Button" {
  interface ButtonPropsVariantOverrides {
    secondary: true;
  }
}

// Create a theme instance with custom colors and typography
export const theme = createTheme({
  colors: baseColors,
  palette: {
    primary: {
      main: baseColors.primary[5],
      light: baseColors.primary[2],
      dark: baseColors.primary[6],
    },
    secondary: {
      main: baseColors.secondary[4],
      light: baseColors.secondary[2],
      dark: baseColors.secondary[6],
    },
    background: {
      default: baseColors.defaultBackground,
      paper: baseColors.defaultBackground,
    },
    text: {
      primary: baseColors.text[6],
      secondary: baseColors.text[1],
    },
  },
  typography: {
    fontFamily: ["Poppins", '"Helvetica Neue"', "Arial", "sans-serif"].join(
      ",",
    ),
    h1: {
      fontSize: "2.125rem",
      fontWeight: 500,
    },
    h2: {
      fontSize: "1.5rem",
      fontWeight: 500,
    },
    h3: {
      fontSize: "1.25rem",
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        "*": {
          scrollbarWidth: "thin",
          scrollbarColor: `${baseColors.secondary[3]} transparent`,
        },
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    // Override component styles here if needed
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none", // Disable uppercase transformation
        },
      },
      variants: [
        {
          props: { variant: "secondary" },
          style: {
            padding: "8px 16px",
            color: baseColors.red,
            backgroundColor: baseColors.grey,
            fontWeight: 600,
            fontSize: 14,
            "&:hover": {
              textDecoration: "underline",
              backgroundColor: baseColors.grey,
            },
          },
        },
      ],
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
        },
      },
    },
  },
});
